import { reportMetrics } from './reportMetrics';
import { flowDuration } from './metrics/creates';
import { getRoute } from './route';

const IGNORED_URL_PATTERNS = [
  '/api/v3/telemetry',
  'shopeepay.sg',
  'shopeepay.com',
];

function shouldIgnore(url) {
  return IGNORED_URL_PATTERNS.some(pattern => url.includes(pattern));
}

function getResourceType(entry) {
  const ext =
    entry.name
      .split('?')[0]
      .split('.')
      .pop()
      ?.toLowerCase() || '';
  if (['js', 'mjs'].includes(ext) || entry.initiatorType === 'script')
    return 'script';
  if (
    ['css'].includes(ext) ||
    entry.initiatorType === 'css' ||
    entry.initiatorType === 'link'
  )
    return 'stylesheet';
  if (
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'avif'].includes(ext) ||
    entry.initiatorType === 'img'
  )
    return 'image';
  if (['woff', 'woff2', 'ttf', 'otf', 'eot'].includes(ext)) return 'font';
  return 'other';
}

// Buffer resource entries and flush periodically. Uses a debounced timer so
// that an idle page doesn't keep flushing tiny batches, while a busy page
// (e.g. SPA route change loading lots of new chunks) flushes regularly.
let pendingMetrics = [];
let flushTimer = null;
const FLUSH_DEBOUNCE_MS = 3000;

function flushMetrics() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pendingMetrics.length === 0) return;
  const batch = pendingMetrics;
  pendingMetrics = [];
  reportMetrics(batch);
}

function queueResourceMetric(metric) {
  pendingMetrics.push(metric);
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushMetrics, FLUSH_DEBOUNCE_MS);
}

function collectResourceEntry(entry) {
  if (shouldIgnore(entry.name)) return;

  const resourceType = getResourceType(entry);
  const duration = entry.responseEnd - entry.startTime;
  const transferSize = entry.transferSize || 0;
  const route = getRoute();

  if (duration > 0) {
    queueResourceMetric(
      flowDuration(duration, {
        flow: 'resource_load',
        resource_type: resourceType,
        route,
      }),
    );
  }

  if (transferSize > 0) {
    queueResourceMetric(
      flowDuration(transferSize, {
        flow: 'resource_size',
        resource_type: resourceType,
        route,
      }),
    );
  }
}

export function initResourceMetrics() {
  if (typeof window === 'undefined' || !window.PerformanceObserver) return;

  try {
    const buffered = performance.getEntriesByType('resource');
    buffered.forEach(collectResourceEntry);

    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        collectResourceEntry(entry);
      }
    });

    observer.observe({ type: 'resource', buffered: false });

    // Force-flush remaining queued metrics before the page is suspended/closed.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushMetrics();
      }
    });
  } catch (e) {
    console.warn('[@shopeepay/reporter] Resource metrics not supported:', e);
  }
}
